import { CircularProgress, Box } from "@mui/material";
import { ApiErrorState } from "../shared/ApiErrorState";
import { TableList } from "./TableList";
import { useTableFetch } from "./hooks/useTableFetch";

interface IGenericTableViewProps<T> {
  onEdit?: (updatedItem: T) => void | Promise<void>;
  onCreateSubmit?: (newItem: Partial<T>) => void | Promise<void>;
  onDeleteConfirm?: (ids: string[]) => void | Promise<void>;
}

export function GenericTableView<T extends object>({
  onEdit,
  onCreateSubmit,
  onDeleteConfirm,
}: IGenericTableViewProps<T>) {
  const {
    data,
    title,
    headers,
    keys,
    idKey,
    FormComponent,
    loading,
    error,
    gameIdsByMatchId,
    gamesByMatchId,
    compactColumns,
    refetchGames,
    refetchData,
    ensureGamesLoaded,
  } = useTableFetch<T>();

  if (loading && !data) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ApiErrorState message={error} variant="alert" />
      </Box>
    );
  }

  return (
    <TableList<T>
      data={data || []}
      title={title}
      headers={headers}
      keys={keys}
      idKey={idKey}
      FormComponent={FormComponent}
      gameIdsByMatchId={gameIdsByMatchId}
      gamesByMatchId={gamesByMatchId}
      compactColumns={compactColumns}
      ensureGamesLoaded={ensureGamesLoaded}
      onEdit={async (item) => {
        await onEdit?.(item);
        refetchData?.();
        refetchGames?.();
      }}
      onCreateSubmit={async (item) => {
        await onCreateSubmit?.(item);
        refetchData?.();
        refetchGames?.();
      }}
      onDeleteConfirm={async (ids) => {
        await onDeleteConfirm?.(ids);
        refetchData?.();
        refetchGames?.();
      }}
    />
  );
}
